import React from 'react';
import { Popover, Tooltip } from 'antd';

import RecordBadge from '../common/RecordBadge';
import { AUTOMATION, CLOSE, LOCK, PERSON_OUTLINE } from '../icons';
import {
	isItemTag,
	isPrivateTag,
	isRestrictedTag,
	tagIdOf,
	tagPathLabel,
	tagRecordOf,
} from '../../utils/tags';

/**
 * THE TAG CHIP (docs/plans/tags.md §5.1; docs/platform-standards.md § Tags).
 *
 * A tag is a record badge like any other: `RecordBadge kind="tag"` — its icon
 * tile on the tag's own color (an identity hue, user data, never theme), then
 * its name. **Nothing else lives inside the pill.** What a tag IS beyond its
 * name is said by a TRAILING AFFORDANCE GROUP outside the pill, in this order:
 *
 *   [tile] Stomach pain   🔒 👤 ⚙ ✕
 *                         │  │  │  └ remove (only when `onRemove` is given)
 *                         │  │  └ applied by a rule (T6a: `automatic`)
 *                         │  └ a PRIVATE tag: "Only you see this"
 *                         └ a RESTRICTED tag: managers put it on and take it off
 *
 * - A PRIVATE tag's pill wears the muted chip fill (`--chip-bg`) where a
 *   collection tag sits on the surface, and the `user` glyph follows it
 *   (ruled d14b) — so it is obvious why it does not show where collection
 *   tags do (shared filters, rules, other people's views).
 * - The ✕ is a real button named "Remove <name>", with a 44px hit area on a
 *   touch screen (tags.css). An `inherited` tag — a private tag a collection
 *   carries, shown on its items — says "From the collection" and has no ✕.
 * - A TOMBSTONE (`tag` null, or an item tag the reader cannot read) reads
 *   "No longer available", muted, never a link. `tombstoneLabel` lets a
 *   surface that knows more say it ("a deleted tag", or the name a History
 *   row recorded) — still muted, still never a link.
 * - An ITEM tag (kind 'item', T2c) is its item's own badge — the card's tile,
 *   the item's live title — resolved through the reader's scope by
 *   `resolveItemTag(tag)` → `{ record, title, kicker?, collection? }` or null
 *   (a tombstone). The surfaces resolve it from the item's `tagTitles`
 *   (utils/itemTags.js itemTagRecord), never from a stored name.
 * - `privateUse`: an ITEM tag this reader applied privately (ruling q2 — an
 *   item tag used outside its family, or anywhere by choice) wears the
 *   private tag's look too: the muted fill and the `user` glyph.
 * - A NEUTRAL chip (`tag.neutral`) is a tag the reader sees applied but whose
 *   collection they cannot see (keepr-api's closed default, owner call B2):
 *   the word "Tag", no color, and a note that says why.
 *
 * - `note`: a few words that say what the tag is DOING here, outside the pill
 *   before the ✕ — the new-item form's "Your active tag" (the active tag,
 *   docs/plans/tags-active-tag-proposal.md). Plain muted text, never a
 *   control, never inside the badge.
 * - `explain` (T6a): where a surface can say WHY a rule applied the tag (the
 *   read view — GET /api/items/{id}/tag-reasons), the `automation` glyph is
 *   a button named "Why …" that opens a pop-over with `explain()`'s content
 *   (called when it first opens). A 44px hit area on a touch screen, like ✕.
 *
 * `TagRow` lays several out: a wrapping row that never scrolls sideways.
 */
export const TOMBSTONE_LABEL = 'No longer available';
const TOMBSTONE_NOTE = 'This tag was deleted, or you can no longer see it.';
export const PRIVATE_NOTE = 'Only you see this';
export const RESTRICTED_NOTE = 'Restricted: only managers put it on items or take it off';
export const INHERITED_NOTE = 'From the collection';
export const AUTOMATIC_NOTE = 'Applied by a rule';
const NEUTRAL_NOTE = 'This tag belongs to a collection you can’t see.';

function Glyph({ icon: Icon, label, testId }) {
	return (
		<Tooltip title={label}>
			<span className="tag-chip__glyph" role="img" aria-label={label} data-testid={testId}>
				<Icon aria-hidden="true" />
			</span>
		</Tooltip>
	);
}

/** A pop-over's content, rendered only once it opens (antd renders content lazily). */
function ExplainContent({ explain }) {
	return <div className="tag-chip__explain">{explain()}</div>;
}

export default function TagBadge({
	tag,
	size = 'xs',
	onRemove,
	removeLabel,
	to,
	onClick,
	inherited = false,
	automatic = false,
	showPath = false,
	resolveItemTag,
	privateUse = false,
	tooltip,
	explain,
	note,
	tombstoneLabel,
	className = '',
	...rest
}) {
	const item = tag && isItemTag(tag) ? (typeof resolveItemTag === 'function' ? resolveItemTag(tag) : null) : null;
	const tombstone = !tag || (isItemTag(tag) && !item);
	const privateTag = !tombstone && (isPrivateTag(tag) || privateUse);
	const restricted = !tombstone && isRestrictedTag(tag);
	const name = tombstone ? (tombstoneLabel || TOMBSTONE_LABEL) : (item ? item.title : tag.name);
	const canRemove = typeof onRemove === 'function' && !inherited;

	const classes = [
		'tag-chip',
		privateTag ? 'tag-chip--private' : '',
		tombstone ? 'tag-chip--tombstone' : '',
		className,
	].filter(Boolean).join(' ');

	const pillTooltip = tooltip
		|| (tombstone ? TOMBSTONE_NOTE : null)
		|| (tag?.neutral ? NEUTRAL_NOTE : null)
		|| (inherited ? INHERITED_NOTE : null)
		|| (showPath && !item ? tagPathLabel(tag) : null)
		|| undefined;

	const badge = item ? (
		<RecordBadge
			kind="item"
			record={item.record}
			kicker={item.kicker}
			title={item.title}
			collection={item.collection || null}
			size={size}
			to={to}
			onClick={onClick}
			tooltip={pillTooltip}
			className={['tag-badge', 'tag-badge--item', privateTag ? 'tag-badge--private' : ''].filter(Boolean).join(' ')}
		/>
	) : (
		<RecordBadge
			kind="tag"
			record={tombstone ? null : tagRecordOf(tag)}
			title={name}
			size={size}
			to={tombstone ? undefined : to}
			onClick={tombstone ? undefined : onClick}
			tooltip={pillTooltip}
			className={['tag-badge', privateTag ? 'tag-badge--private' : '', tombstone ? 'tag-badge--tombstone' : ''].filter(Boolean).join(' ')}
		/>
	);

	const affordances = [];
	if (restricted) affordances.push(<Glyph key="restricted" icon={LOCK} label={RESTRICTED_NOTE} testId="tag-restricted" />);
	if (privateTag) affordances.push(<Glyph key="private" icon={PERSON_OUTLINE} label={PRIVATE_NOTE} testId="tag-private" />);
	// No "Why" on a chip the reader cannot name (the API answers reasons only
	// for tags the reader sees named on the item).
	if (automatic && !tombstone && !tag?.neutral && typeof explain === 'function') {
		affordances.push(
			<Popover
				key="automatic"
				trigger="click"
				placement="bottomLeft"
				title={AUTOMATIC_NOTE}
				content={<ExplainContent explain={explain} />}
			>
				<button
					type="button"
					className="tag-chip__why"
					aria-label={`Why is ${name} on this item?`}
					title={`${AUTOMATIC_NOTE}. Why?`}
					data-testid="tag-automatic"
					onClick={event => event.stopPropagation()}
				>
					<AUTOMATION aria-hidden="true" />
				</button>
			</Popover>
		);
	} else if (automatic && !tombstone) {
		affordances.push(<Glyph key="automatic" icon={AUTOMATION} label={AUTOMATIC_NOTE} testId="tag-automatic" />);
	}
	if (note && !tombstone) {
		affordances.push(<span key="note" className="tag-chip__note" data-testid="tag-note">{note}</span>);
	}
	if (canRemove) {
		affordances.push(
			<button
				key="remove"
				type="button"
				className="tag-chip__remove"
				aria-label={removeLabel || `Remove ${name}`}
				title={removeLabel || `Remove ${name}`}
				onClick={event => { event.stopPropagation(); onRemove(tag, event); }}
			>
				<CLOSE aria-hidden="true" />
			</button>
		);
	}

	return (
		<span className={classes} data-tag-id={tag ? tagIdOf(tag) || undefined : undefined} {...rest}>
			{badge}
			{affordances.length ? <span className="tag-chip__affordances">{affordances}</span> : null}
		</span>
	);
}

/**
 * Several tags in a row: wraps onto as many lines as it needs and never
 * scrolls sideways (docs/platform-standards.md § Tags). `tags` are rendered
 * in the order given; `renderTag(tag)` overrides one chip.
 */
export function TagRow({ tags = [], renderTag, className = '', label, empty = null, ...chipProps }) {
	const list = Array.isArray(tags) ? tags : [];
	if (!list.length) return empty;
	return (
		<ul className={['tag-row', className].filter(Boolean).join(' ')} aria-label={label}>
			{list.map((tag, index) => (
				<li key={tagIdOf(tag) || `tag-${index}`} className="tag-row__item">
					{typeof renderTag === 'function' ? renderTag(tag) : <TagBadge tag={tag} {...chipProps} />}
				</li>
			))}
		</ul>
	);
}
